import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IdLabel } from '../../components/atoms.js';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { SearchEmptyNotice } from '../../components/search-empty-notice.js';
import {
  type ChainStatus,
  chainLinkKey,
  chainProgress,
  chainStatus,
  groupByChain,
} from '../../lib/chain-groups.js';
import { pickLang } from '../../lib/pick-lang.js';
import {
  type PlaylistEntryRef,
  playlistCounts,
  playlistEntryKey,
} from '../../lib/playlist-groups.js';
import { matchesQuery } from '../../lib/row-filter.js';
import { type ChainLink, type ChainView, type PlaylistEntry, runsApi } from '../../lib/runs-api.js';
import { rejectedDefectOf } from './chain-bar.js';
import { FixCheckCards, type FixCheckListProps, fixCheckMatches } from './fix-check-card.js';
import { PlaylistCard, PlaylistToolbar } from './playlist.js';

// =================================================================================================
// #128: „Meine Tests" NACH E2E-PROZESS — die dritte Ansicht neben Status und Baum.
//
// Eine Gruppe je Kette, darin die GANZE Kette in Server-Reihenfolge: eigene Glieder als dieselbe
// `PlaylistCard` wie in der Status-Ansicht (Auswahl, Sperre und „wartet auf" verhalten sich also
// identisch), fremde Glieder grau und nicht bedienbar — mit Tester und letztem Ergebnis. Der
// Tester sieht so, wo die Kette gerade steht, ohne jede Kette einzeln im Ausführungsfeld zu öffnen.
//
// Die Kettensichten kommen in EINER Abfrage (`runsApi.chains`, Task 4) statt einer je Kette. Der
// Query-Key beginnt mit `['e2e-chain', projectId, planId]` — genau dieses Präfix invalidiert das
// ExecutionPanel nach jedem Lauf (Task 3), die Gruppen ziehen also ohne eigenes Zutun nach.
// =================================================================================================

// Schlüssel der Gruppe „Ohne E2E-Prozess" im Klappzustand. e2e_ids sind UUIDs — kein Konflikt.
const NO_CHAIN = 'none';

// Liest die AUSDRÜCKLICHEN Klapp-Entscheidungen fail-soft aus sessionStorage (Muster wie
// `readCollapsed` in playlist.tsx). Gespeichert werden nur Klicks; der Default („erledigte Ketten
// zu") wird beim Rendern aus dem Kettenstatus abgeleitet — so klappt eine Kette, die später fertig
// wird, ohne gespeicherte Entscheidung auch von selbst zu.
function readOverrides(stored: string | null): Record<string, boolean> {
  if (stored) {
    try {
      const parsed: unknown = JSON.parse(stored);
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return Object.fromEntries(
          Object.entries(parsed).filter((pair): pair is [string, boolean] => {
            return typeof pair[1] === 'boolean';
          }),
        );
      }
    } catch {
      // durchfallen zum Default
    }
  }
  return {};
}

const STATUS_CLASS: Record<ChainStatus['kind'], string> = {
  done: 'text-status-pass',
  // „steht" = ein Fehler- oder Blockiert-Lauf hält die Kette an — Farbe wie das Ergebnis selbst
  // (RESULT_TONE in atoms.tsx: error und blocked sind beide `fail`).
  stopped: 'text-status-fail',
  at: 'text-text-muted',
};

function ForeignLinkRow({ link }: { link: ChainLink }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const title = pickLang(lang, link.title_de, link.title_en);
  const rejectedDefect = rejectedDefectOf(link.last_run);
  return (
    // Bewusst kein Button und kein Klick-Handler: das Glied gehört einem anderen Tester, es gibt
    // hier nichts zu bedienen. `opacity-60` wie die gesperrte Karte — dieselbe Bildsprache für
    // „sichtbar, aber nicht deins/nicht jetzt".
    <div
      data-chain-link
      className="flex w-full flex-col gap-0.5 rounded-card border border-dashed border-[var(--border)] px-3 py-1.5 text-[11px] text-text-muted opacity-60"
    >
      <span className="flex items-center gap-1">
        <span>{t('exec.chainGroup.step', { sequence: link.sequence })}</span>
        <span aria-hidden="true">·</span>
        <IdLabel id={link.human_id} />
      </span>
      <span className="line-clamp-2 text-text-body">{title}</span>
      <span>
        {link.assigned_tester_name ?? t('exec.chain.unknownTester')}
        {' · '}
        {/* #190: abgelehnter Fehler-Lauf (laut Server, `rejected`) = offen statt „Fehler" (Kette
            steht trotzdem). */}
        {rejectedDefect
          ? t('exec.rejectedDefect', { id: rejectedDefect.human_id })
          : link.last_run
            ? t(`results.${link.last_run.result}`)
            : t('exec.chain.notRunYet')}
      </span>
    </div>
  );
}

// Der Status-Text im Gruppenkopf. Als eigene Komponente, damit sie `t` selbst holt — die
// Signatur von i18nexts `TFunction` lässt sich nicht sauber als Parameter durchreichen.
function ChainStatusLabel({
  view,
  status,
  ownKeys,
}: {
  view: ChainView;
  status: ChainStatus;
  ownKeys: Set<string>;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  let text: string;
  if (status.kind === 'done') {
    text = t('exec.chainGroup.done');
  } else {
    const l = status.link;
    // Der ZUGEWIESENE Tester des Glieds — bei wem es liegt bzw. wessen Schritt steht. Nicht der
    // Ausführende des letzten Laufs: bei einer Vertretung (PLAN-5) bleibt die Zuständigkeit beim
    // Zugewiesenen.
    const name = l.assigned_tester_name ?? t('exec.chain.unknownTester');
    if (status.kind === 'stopped') {
      text = t('exec.chainGroup.stoppedAt', {
        sequence: l.sequence,
        title: pickLang(lang, l.title_de, l.title_en),
        name,
      });
    } else if (ownKeys.has(chainLinkKey(view, l))) {
      text = t('exec.chainGroup.atYou', { sequence: l.sequence });
    } else {
      text = t('exec.chainGroup.atStep', { name, sequence: l.sequence });
    }
  }
  return <span className={STATUS_CLASS[status.kind]}>{text}</span>;
}

export function ChainGroups({
  projectId,
  planId,
  entries,
  isLoading,
  selectedKey,
  onSelect,
  canViewDefect,
  onOpenDefect,
  search,
  onSearchChange,
  footer,
  fixChecks,
  selectedFixCheckId,
  onSelectFixCheck,
  onRetryFixChecks,
  fixChecksLoading = false,
}: FixCheckListProps & {
  projectId: string;
  planId: string;
  entries: PlaylistEntry[];
  isLoading: boolean;
  selectedKey: string | null;
  onSelect: (ref: PlaylistEntryRef) => void;
  canViewDefect: boolean;
  // #192: Retest-Chip öffnet für Tester die Nur-Lesen-Seitenleiste (s. PlaylistCard).
  onOpenDefect?: ((defectId: string) => void) | undefined;
  search: string;
  onSearchChange: (v: string) => void;
  footer?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';

  // Sortiert und eindeutig: derselbe Satz Ketten ergibt denselben Query-Key, egal in welcher
  // Reihenfolge die Playlist sie liefert.
  const ids = [
    ...new Set(entries.map((e) => e.e2e_id).filter((id): id is string => id !== null)),
  ].sort();
  const chainsQuery = useQuery({
    queryKey: ['e2e-chain', projectId, planId, 'batch', ids.join(',')],
    queryFn: () => runsApi.chains(projectId, planId, ids),
    enabled: Boolean(projectId) && Boolean(planId) && ids.length > 0,
  });
  const views = chainsQuery.data ?? [];

  const storageKey = planId ? `tf.playlist.${planId}.chainsCollapsed` : null;
  const [overrides, setOverrides] = useState<Record<string, boolean>>(() =>
    readOverrides(storageKey ? sessionStorage.getItem(storageKey) : null),
  );
  // Planwechsel ist ein Prop-Wechsel, kein Remount — derselbe Grund wie in playlist.tsx (#93 Fix).
  useEffect(() => {
    setOverrides(readOverrides(storageKey ? sessionStorage.getItem(storageKey) : null));
  }, [storageKey]);

  const toggle = (key: string, isCollapsed: boolean) => {
    setOverrides((prev) => {
      const next = { ...prev, [key]: !isCollapsed };
      if (storageKey) sessionStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  // Auswahl klappt ihre Gruppe auf (wie #93 in der Status-Ansicht). Refs statt Deps: ein Refetch
  // liefert neue Objekt-Referenzen und dürfte eine bewusst zugeklappte Gruppe nicht wieder
  // aufreißen (#93 Fix, Finding 2). `viewsReady` steht in den Deps, damit eine Auswahl, die VOR
  // dem Laden der Ketten feststand (Deeplink), ihre Gruppe trotzdem findet — es wechselt nur
  // einmal von false auf true, ein Refetch berührt es nicht.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const viewsRef = useRef(views);
  viewsRef.current = views;
  const viewsReady = chainsQuery.data !== undefined;
  // biome-ignore lint/correctness/useExhaustiveDependencies: viewsReady stößt bewusst nur einmal nach dem Laden an, s. o.
  useEffect(() => {
    if (!selectedKey) return;
    const selected = entriesRef.current.find((e) => playlistEntryKey(e) === selectedKey);
    if (!selected) return;
    const inView =
      selected.e2e_id !== null && viewsRef.current.some((v) => v.e2e_id === selected.e2e_id);
    const key = inView && selected.e2e_id ? selected.e2e_id : NO_CHAIN;
    setOverrides((prev) => {
      if (prev[key] === false) return prev;
      const next = { ...prev, [key]: false };
      if (storageKey) sessionStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  }, [selectedKey, storageKey, viewsReady]);

  const trimmed = search.trim();
  const isVisible = (e: PlaylistEntry) => matchesQuery(trimmed, e.human_id, e.title_de, e.title_en);
  // #194: Karten „Fehler prüfen" — dieselbe Suche, derselbe Zähler wie in der Status-Ansicht.
  const visibleChecks = fixChecks.filter((c) => fixCheckMatches(trimmed, c));
  const visibleCount = entries.filter(isVisible).length + visibleChecks.length;
  const hiddenCount = entries.length + fixChecks.length - visibleCount;
  // Zähler unverändert aus playlistCounts — dieselben Zahlen wie in der Status-Ansicht (#194: plus
  // sichtbare Karten).
  const entryCounts = playlistCounts(entries, isVisible);
  const counts = { ...entryCounts, open: entryCounts.open + visibleChecks.length };
  const hasContent = entries.length > 0 || fixChecks.length > 0;
  // #194: wie in playlist.tsx — ohne Einträge erst nach der Antwort der Fehlerprüfungen „leer".
  const loading = isLoading || (fixChecksLoading && !hasContent);

  const byKey = new Map(entries.map((e) => [playlistEntryKey(e), e]));
  const { chains, noChain } = groupByChain(entries, views);
  const visibleNoChain = noChain.filter(isVisible);
  const noChainCollapsed = overrides[NO_CHAIN] ?? false;

  const renderCard = (entry: PlaylistEntry, showChain: boolean) => (
    <PlaylistCard
      key={playlistEntryKey(entry)}
      entry={entry}
      selected={playlistEntryKey(entry) === selectedKey}
      onSelect={() => onSelect({ test_case_id: entry.test_case_id, e2e_id: entry.e2e_id })}
      canViewDefect={canViewDefect}
      onOpenDefect={onOpenDefect}
      showChain={showChain}
    />
  );

  const chainsLoading = ids.length > 0 && chainsQuery.isLoading;

  return (
    <div className="flex w-[360px] shrink-0 flex-col border-r border-[var(--border)]">
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-3">
        {(loading || chainsLoading) && (
          <div className="text-sm text-text-muted">{t('common.loading')}</div>
        )}
        {/* #194/#89: eine gescheiterte Abfrage der Fehlerprüfungen ist kein „nichts zu tun". */}
        {onRetryFixChecks && <QueryErrorNotice compact onRetry={onRetryFixChecks} />}
        {!loading && !hasContent && !onRetryFixChecks && (
          <div className="text-sm text-text-muted">{t('exec.empty')}</div>
        )}
        {/* #89: ein gescheiterter Ketten-Abruf ist kein Leerzustand. */}
        {chainsQuery.isError && <QueryErrorNotice onRetry={() => chainsQuery.refetch()} />}
        {!isLoading && !chainsLoading && !chainsQuery.isError && hasContent && (
          <>
            <PlaylistToolbar
              search={search}
              onSearchChange={onSearchChange}
              counts={counts}
              hiddenCount={hiddenCount}
            />
            {trimmed !== '' && visibleCount === 0 && <SearchEmptyNotice query={trimmed} />}
            {/* #194 (B5): Karten „Fehler prüfen" als eigener Block ÜBER den Ketten — eine
                Fehlerprüfung gehört zu keiner Kette (der Testfall läuft nicht neu). Überschrift wie
                die Gruppe „Retest", aber mit eigenem Wort: der Block zählt nur Fehlerprüfungen, nicht
                die Retest-Glieder der Ketten darunter. */}
            {visibleChecks.length > 0 && (
              <section className="flex flex-col gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wide text-status-fail">
                  {t('fixCheck.group', { count: visibleChecks.length })}
                </span>
                <FixCheckCards
                  checks={visibleChecks}
                  selectedFixCheckId={selectedFixCheckId}
                  onSelectFixCheck={onSelectFixCheck}
                />
              </section>
            )}
            {chains.map(({ view, ownKeys }) => {
              // Suche: die Gruppe erscheint, sobald EIN eigener Eintrag passt. Fremde Zeilen
              // bleiben in einer gezeigten Gruppe stehen — sie sind Kontext, kein Suchtreffer.
              const ownVisible = [...ownKeys].some((k) => {
                const e = byKey.get(k);
                return e !== undefined && isVisible(e);
              });
              if (!ownVisible) return null;
              const status = chainStatus(view);
              const progress = chainProgress(view);
              const collapsed = overrides[view.e2e_id] ?? status.kind === 'done';
              const name = pickLang(lang, view.name_de, view.name_en);
              return (
                <section key={view.e2e_id} className="flex flex-col gap-2">
                  <button
                    type="button"
                    aria-expanded={!collapsed}
                    onClick={() => toggle(view.e2e_id, collapsed)}
                    className="flex w-full flex-col items-start gap-0.5 text-left"
                  >
                    <span className="flex w-full items-start gap-1.5 text-xs font-bold text-text-primary">
                      <span aria-hidden="true" className="text-text-muted">
                        {collapsed ? '▸' : '▾'}
                      </span>
                      <span className="line-clamp-2">{`${view.human_id} · ${name}`}</span>
                    </span>
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 pl-4 text-[11px]">
                      <span className="text-text-muted">
                        {t('exec.chainGroup.progress', progress)}
                      </span>
                      <ChainStatusLabel view={view} status={status} ownKeys={ownKeys} />
                    </span>
                  </button>
                  {!collapsed &&
                    view.links.map((l) => {
                      const key = chainLinkKey(view, l);
                      const own = ownKeys.has(key) ? byKey.get(key) : undefined;
                      // Schlüssel mit Sequenz: derselbe Testfall darf in einer Kette zweimal
                      // vorkommen, sein Playlist-Eintrag ist aber nur einer.
                      const rowKey = `${l.test_case_id}|${l.sequence}`;
                      if (own) {
                        // Eigene Glieder folgen der Suche wie in der Status-Ansicht.
                        if (!isVisible(own)) return null;
                        return <div key={rowKey}>{renderCard(own, false)}</div>;
                      }
                      return <ForeignLinkRow key={rowKey} link={l} />;
                    })}
                </section>
              );
            })}
            {visibleNoChain.length > 0 && (
              <section className="flex flex-col gap-2">
                <button
                  type="button"
                  aria-expanded={!noChainCollapsed}
                  onClick={() => toggle(NO_CHAIN, noChainCollapsed)}
                  className="flex w-full items-start gap-1.5 text-left text-xs font-bold text-text-primary"
                >
                  <span aria-hidden="true" className="text-text-muted">
                    {noChainCollapsed ? '▸' : '▾'}
                  </span>
                  <span>{t('exec.chainGroup.noChain')}</span>
                </button>
                {!noChainCollapsed && visibleNoChain.map((e) => renderCard(e, true))}
              </section>
            )}
          </>
        )}
      </div>
      {footer && <div className="border-t border-[var(--border)] p-3">{footer}</div>}
    </div>
  );
}
